import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, ClipboardList, Upload, X, ChevronDown, ChevronRight, Calendar, CalendarDays, User, LayoutList, LayoutGrid, MoreVertical, Flag, GanttChart, RefreshCw, Bell, CheckCheck, MapPin, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { tasksApi, staffApi, talentApi, eventsApi } from "@/lib/unifiedApi";
import ExportDialog from "@/components/ExportDialog";
import { storageService } from "@/lib/storage";
import { toast } from "sonner";
import { sendNotification } from "@/lib/messaging";

// Map staff display name → userId (must match AuthContext user ids)
const STAFF_NAME_TO_USER_ID = {
  'Admin': 'admin',
  'Font':  'font',
  'Yok':   'yok',
  'Game':  'game',
  'Benz':  'benz',
  'Prai':  'plai',
  'Ice':   'ice',
  'Mild':  'mild',
};
import {
  format,
  parseISO,
  isToday,
  compareAsc,
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  addWeeks,
  isSameDay,
} from "date-fns";

// ── Multi-staff helpers ───────────────────────────────────────────
// Normalise assigned_to → always an array (handles legacy string values)
const getAssignees = (task) => {
  if (!task.assigned_to) return [];
  if (Array.isArray(task.assigned_to)) return task.assigned_to.filter(Boolean);
  return [task.assigned_to]; // backward compat: old string → [string]
};

// Dropdown with checkboxes for picking multiple staff members
const MultiStaffSelect = ({ value = [], onChange, options = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (name) =>
    onChange(value.includes(name) ? value.filter((n) => n !== name) : [...value, name]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#BFFF00]/40 focus:border-[#BFFF00] min-h-[38px] transition-colors"
      >
        <div className="flex-1 min-w-0 flex flex-wrap gap-1">
          {value.length === 0 ? (
            <span className="text-gray-400">Select staff</span>
          ) : (
            value.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
              >
                {name}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(name); }}
                  className="text-blue-400 hover:text-blue-700 leading-none"
                >
                  <X size={10} />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDown size={15} className="text-gray-400 flex-shrink-0 ml-2" />
      </button>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-52 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No staff available</div>
          ) : (
            options.map((s) => {
              const checked = value.includes(s.name);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.name)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-100 text-sm text-left transition-colors"
                >
                  <div
                    className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{
                      background: checked ? "#3b82f6" : "white",
                      borderColor: checked ? "#3b82f6" : "#d1d5db",
                    }}
                  >
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-700">{s.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const TASK_EXPORT_COLUMNS = [
  { key: 'title',               label: 'Title' },
  { key: 'status',              label: 'Status', format: (v, row) => v || (row.completed ? 'done' : 'todo') },
  { key: 'priority',            label: 'Priority' },
  { key: 'tag',                 label: 'Tag' },
  { key: 'assigned_to',         label: 'Assigned To' },
  { key: 'start_date',          label: 'Start Date' },
  { key: 'date',                label: 'Due Date' },
  { key: 'location',            label: 'Location' },
  { key: 'transportation_cost', label: 'Transport Cost (฿)' },
  { key: 'description',         label: 'Description' },
];

const TasksPage = () => {
  const { user } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [talent, setTalent] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ganttTooltip, setGanttTooltip] = useState(null); // { task, x, y }
  const [editingTask, setEditingTask] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // calendar | list | board | gantt | mytasks
  const [myTasksDate, setMyTasksDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [staffFilter, setStaffFilter] = useState("all"); // Staff filter
  const [ganttMonth, setGanttMonth] = useState(startOfMonth(new Date()));
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  const [calSubMode, setCalSubMode] = useState("month"); // "month" | "week"
  const [calendarWeek, setCalendarWeek] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    todo: true,
    in_progress: true,
    done: true,
  });

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: format(new Date(), "yyyy-MM-dd"),
    date: format(new Date(), "yyyy-MM-dd"),
    assigned_to: [],
    location: "",
    transportation_cost: 0,
    receipt_url: "",
    status: "todo",
    completed: false,
    tag: "",
    priority: "mid",
    recurrence: { type: "none" },
  });

  const statuses = [
    { value: "todo", label: "To Do" },
    { value: "in_progress", label: "In Progress" },
    { value: "done", label: "Done" },
  ];

  const priorities = [
    { value: "high", label: "High", color: "bg-red-100 text-red-600" },
    { value: "mid", label: "Mid", color: "bg-amber-100 text-amber-600" },
    { value: "low", label: "Low", color: "bg-emerald-100 text-emerald-600" },
  ];

  const tags = [
    { value: "work", label: "Work", color: "bg-blue-100 text-blue-600" },
    { value: "health", label: "Health", color: "bg-emerald-100 text-emerald-600" },
    { value: "personal", label: "Personal", color: "bg-purple-100 text-purple-600" },
    { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-600" },
    { value: "meeting", label: "Meeting", color: "bg-amber-100 text-amber-600" },
  ];

  // Export filters
  const [exportFilters, setExportFilters] = useState({
    status: 'all', priority: 'all', tag: 'all', dateFrom: '', dateTo: '',
  });
  const exportData = tasks
    .filter((task) => {
      const taskStatus = task.status || (task.completed ? 'done' : 'todo');
      if (exportFilters.status !== 'all' && taskStatus !== exportFilters.status) return false;
      if (exportFilters.priority !== 'all' && task.priority !== exportFilters.priority) return false;
      if (exportFilters.tag !== 'all' && task.tag !== exportFilters.tag) return false;
      if (exportFilters.dateFrom && task.date < exportFilters.dateFrom) return false;
      if (exportFilters.dateTo && task.date > exportFilters.dateTo) return false;
      return true;
    })
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  useEffect(() => {
    const unsubscribeTasks = tasksApi.subscribe((data) => {
      setTasks(data);
      setLoading(false);
    });

    const unsubscribeStaff = staffApi.subscribe((data) => {
      setStaff(data);
    });

    const unsubscribeTalent = talentApi.subscribe((data) => {
      setTalent(data);
    });

    const unsubscribeEvents = eventsApi.subscribe((data) => {
      setEvents(data);
    });

    const timeout = setTimeout(() => setLoading(false), 2000);

    return () => {
      unsubscribeTasks();
      unsubscribeStaff();
      unsubscribeTalent();
      unsubscribeEvents();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await tasksApi.update(editingTask.id, formData);
        toast.success("Task updated successfully");
        // Notify only newly-added assignees (people who weren't assigned before)
        const prevAssignees = getAssignees(editingTask);
        const added = (formData.assigned_to || []).filter((n) => !prevAssignees.includes(n));
        if (added.length > 0) {
          const assignedUserIds = added
            .map((name) => STAFF_NAME_TO_USER_ID[name])
            .filter(Boolean);
          if (assignedUserIds.length > 0) {
            sendNotification({
              type: 'task_assigned',
              title: 'งานที่ถูก assign ให้คุณ',
              body: formData.title || 'มีงานที่ถูก assign ให้คุณ',
              targetUserIds: assignedUserIds,
              data: { type: 'task_assigned', url: '/tasks' },
            });
          }
        }
      } else {
        await tasksApi.create(formData);
        toast.success("Task created successfully");
        // Notify all assigned staff for new tasks
        const assignedUserIds = (formData.assigned_to || [])
          .map((name) => STAFF_NAME_TO_USER_ID[name])
          .filter(Boolean);
        if (assignedUserIds.length > 0) {
          sendNotification({
            type: 'task_assigned',
            title: 'งานใหม่สำหรับคุณ',
            body: formData.title || 'มีงานที่ถูก assign ให้คุณ',
            targetUserIds: assignedUserIds,
            data: { type: 'task_assigned', url: '/tasks' },
          });
        }
      }
      resetForm();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await tasksApi.delete(id);
      toast.success("Task deleted successfully");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title || "",
      description: task.description || "",
      start_date: task.start_date || task.date || "",
      date: task.date || "",
      assigned_to: Array.isArray(task.assigned_to)
        ? task.assigned_to.filter(Boolean)
        : task.assigned_to ? [task.assigned_to] : [],
      location: task.location || "",
      transportation_cost: task.transportation_cost || 0,
      receipt_url: task.receipt_url || "",
      status: task.status || "todo",
      completed: task.completed || false,
      tag: task.tag || "",
      priority: task.priority || "mid",
      recurrence: task.recurrence || { type: "none" },
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: "",
      description: "",
      start_date: format(new Date(), "yyyy-MM-dd"),
      date: format(new Date(), "yyyy-MM-dd"),
      assigned_to: [],
      location: "",
      transportation_cost: 0,
      receipt_url: "",
      status: "todo",
      completed: false,
      tag: "",
      priority: "mid",
      recurrence: { type: "none" },
    });
    setDialogOpen(false);
  };

  // ── Recurrence helpers ────────────────────────────────────────
  const WEEKDAYS_TH = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

  const getRecurrenceLabel = (rec) => {
    if (!rec || rec.type === "none") return null;
    if (rec.type === "daily") return "ทุกวัน";
    if (rec.type === "weekly") {
      const days = (rec.days || []).map((d) => WEEKDAYS_TH[d]).join(" ");
      return `ทุกสัปดาห์ (${days || "-"})`;
    }
    if (rec.type === "monthly") return `วันที่ ${rec.date || 1} ทุกเดือน`;
    if (rec.type === "monthly_range") return `วันที่ ${rec.start || 1}–${rec.end || 1} ทุกเดือน`;
    return null;
  };

  // Returns array of { barStart, barEnd } for this month, or null if not recurring
  const getRecurringBars = (task, mStart, mEnd) => {
    const r = task.recurrence;
    if (!r || r.type === "none") return null;
    const year = mStart.getFullYear();
    const month = mStart.getMonth();
    const lastDay = mEnd.getDate();

    if (r.type === "daily") {
      return [{ barStart: mStart, barEnd: mEnd }];
    }
    if (r.type === "weekly") {
      const wDays = r.days || [];
      return Array.from({ length: lastDay }, (_, i) => {
        const d = new Date(year, month, i + 1);
        return wDays.includes(d.getDay()) ? { barStart: d, barEnd: d } : null;
      }).filter(Boolean);
    }
    if (r.type === "monthly") {
      const day = Math.min(Math.max(1, r.date || 1), lastDay);
      const d = new Date(year, month, day);
      return [{ barStart: d, barEnd: d }];
    }
    if (r.type === "monthly_range") {
      const s = Math.min(Math.max(1, r.start || 1), lastDay);
      const e = Math.min(Math.max(s, r.end || s), lastDay);
      return [{ barStart: new Date(year, month, s), barEnd: new Date(year, month, e) }];
    }
    return null;
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const result = await storageService.uploadFile(file, 'receipts', () => {});
      setFormData({ ...formData, receipt_url: result.url });
      toast.success('Receipt uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload receipt');
    } finally {
      setUploading(false);
    }
  };

  const toggleTaskComplete = async (task) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    const newCompleted = newStatus === "done";
    
    setTasks(prev => prev.map(t => 
      t.id === task.id ? { ...t, status: newStatus, completed: newCompleted } : t
    ));
    
    try {
      await tasksApi.update(task.id, { 
        ...task, 
        completed: newCompleted,
        status: newStatus
      });
    } catch (error) {
      setTasks(prev => prev.map(t => 
        t.id === task.id ? task : t
      ));
      toast.error("Failed to update task");
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Filter tasks by staff (works for both string and array assigned_to)
  const filteredTasks = staffFilter === "all"
    ? tasks
    : tasks.filter(t => getAssignees(t).includes(staffFilter));

  // Group tasks by status
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === "todo" || (!t.status && !t.completed)).sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return compareAsc(parseISO(a.date), parseISO(b.date));
    }),
    in_progress: filteredTasks.filter(t => t.status === "in_progress").sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return compareAsc(parseISO(a.date), parseISO(b.date));
    }),
    done: filteredTasks.filter(t => t.status === "done" || (!t.status && t.completed)).sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return compareAsc(parseISO(a.date), parseISO(b.date));
    }),
  };

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const overdueCount = filteredTasks.filter(
    (t) => t.date && t.date < todayStr && t.status !== "done" && !t.completed
  ).length;

  const getPriorityStyle = (priority) => {
    const p = priorities.find(pr => pr.value === priority);
    return p?.color || "bg-gray-100 text-gray-600";
  };

  const getTagStyle = (tag) => {
    const t = tags.find(tg => tg.value === tag);
    return t?.color || "bg-gray-100 text-gray-600";
  };

  const getTagLabel = (tag) => {
    const t = tags.find(tg => tg.value === tag);
    return t?.label || tag;
  };

  const getPriorityLabel = (priority) => {
    const p = priorities.find(pr => pr.value === priority);
    return p?.label || priority;
  };

  // ── Talent avatar helpers ──────────────────────────────────────
  // Map talent name → talent record (has .avatar URL)
  const talentByName = Object.fromEntries(
    talent.filter((t) => t.name).map((t) => [t.name, t])
  );

  const getInitials = (name = "") =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  // Render overlapping avatar circles for multiple assignees
  const renderAvatarStack = (assignees, { size = 20, max = 3, borderColor = "white", fallbackBg = "#3B82F6" } = {}) => {
    if (!assignees || assignees.length === 0) return null;
    const shown = assignees.slice(0, max);
    const extra = assignees.length - shown.length;
    return (
      <div className="flex items-center flex-shrink-0">
        {shown.map((name, i) => {
          const person = talentByName[name];
          const avatarUrl = person?.avatar;
          const baseStyle = {
            width: size, height: size, borderRadius: "50%",
            border: `1.5px solid ${borderColor}`,
            marginLeft: i > 0 ? -(size * 0.28) : 0,
            flexShrink: 0,
          };
          return avatarUrl ? (
            <img
              key={name}
              src={avatarUrl}
              alt={name}
              title={name}
              style={{ ...baseStyle, objectFit: "cover" }}
            />
          ) : (
            <div
              key={name}
              title={name}
              style={{
                ...baseStyle,
                background: fallbackBg,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: size * 0.38,
                fontWeight: 700,
              }}
            >
              {getInitials(name).slice(0, 1)}
            </div>
          );
        })}
        {extra > 0 && (
          <div
            title={assignees.slice(max).join(", ")}
            style={{
              width: size, height: size, borderRadius: "50%",
              border: `1.5px solid ${borderColor}`,
              marginLeft: -(size * 0.28),
              background: "#e5e7eb", color: "#6b7280",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: size * 0.32, fontWeight: 700, flexShrink: 0,
            }}
          >
            +{extra}
          </div>
        )}
      </div>
    );
  };

  // Bright solid color palette — inspired by modern project timelines
  const GANTT_TAG_COLORS = [
    { bg: "#4F7EF7", shadow: "rgba(79,126,247,0.3)" },   // blue
    { bg: "#F9645A", shadow: "rgba(249,100,90,0.3)" },    // coral-red
    { bg: "#F97316", shadow: "rgba(249,115,22,0.3)" },    // orange
    { bg: "#06B6D4", shadow: "rgba(6,182,212,0.3)" },     // cyan
    { bg: "#10B981", shadow: "rgba(16,185,129,0.3)" },    // teal
    { bg: "#A855F7", shadow: "rgba(168,85,247,0.3)" },    // purple
    { bg: "#F59E0B", shadow: "rgba(245,158,11,0.3)" },    // amber
    { bg: "#EC4899", shadow: "rgba(236,72,153,0.3)" },    // pink
  ];

  const getGanttBarStyle = (task) => {
    const status = task.status || (task.completed ? "done" : "todo");
    // Color by tag first (gives variety), fall back to status
    const tagList = [
      "research","strategy","ux","ui","design","content","development","dev",
      "interaction","sales","report","hr","finance","marketing","ops","event",
    ];
    const tagIdx = task.tag ? tagList.indexOf(task.tag.toLowerCase()) : -1;
    if (tagIdx >= 0) {
      const c = GANTT_TAG_COLORS[tagIdx % GANTT_TAG_COLORS.length];
      return { bg: c.bg, shadow: c.shadow, text: "text-white", avatarRing: "border-white/60", done: status === "done" };
    }
    // Status fallback
    if (status === "done")       return { bg: "#10B981", shadow: "rgba(16,185,129,0.3)",  text: "text-white", avatarRing: "border-white/60", done: true };
    if (status === "in_progress") return { bg: "#F97316", shadow: "rgba(249,115,22,0.3)", text: "text-white", avatarRing: "border-white/60", done: false };
    return                               { bg: "#4F7EF7", shadow: "rgba(79,126,247,0.3)", text: "text-white", avatarRing: "border-white/60", done: false };
  };

  // ── Gantt View (monthly scope) ────────────────────────────────
  const renderGanttView = () => {
    const monthStart = startOfMonth(ganttMonth);
    const monthEnd = endOfMonth(ganttMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Build expanded task list — recurring tasks become virtual instances
    const monthTasks = [];
    for (const t of filteredTasks) {
      const recurBars = getRecurringBars(t, monthStart, monthEnd);
      if (recurBars && recurBars.length > 0) {
        recurBars.forEach((bar, i) => {
          monthTasks.push({ ...t, _barStart: bar.barStart, _barEnd: bar.barEnd, _recurring: true, _key: `${t.id}-r${i}` });
        });
      } else if (t.date) {
        const due = parseISO(t.date);
        const start = t.start_date ? parseISO(t.start_date) : due;
        if (start <= monthEnd && due >= monthStart) {
          monthTasks.push(t);
        }
      }
    }
    monthTasks.sort((a, b) => {
      const aD = a._barStart || (a.start_date ? parseISO(a.start_date) : (a.date ? parseISO(a.date) : new Date(0)));
      const bD = b._barStart || (b.start_date ? parseISO(b.start_date) : (b.date ? parseISO(b.date) : new Date(0)));
      return aD - bD;
    });

    const DAY_W = 38; // px per day cell
    const NAME_W = 150;

    // Events that overlap with this month
    const monthEvents = events
      .filter((ev) => {
        const start = parseISO(ev.date_start || ev.date);
        const end = ev.date_end ? parseISO(ev.date_end) : start;
        return start <= monthEnd && end >= monthStart;
      })
      .sort((a, b) => (a.date_start || a.date || "").localeCompare(b.date_start || b.date || ""));

    // Helper: compute pixel band for an event within the month grid
    const getEventBand = (ev) => {
      const evStart = parseISO(ev.date_start || ev.date);
      const evEnd = ev.date_end ? parseISO(ev.date_end) : evStart;
      const clampedStart = evStart < monthStart ? monthStart : evStart;
      const clampedEnd = evEnd > monthEnd ? monthEnd : evEnd;
      const startIdx = differenceInCalendarDays(clampedStart, monthStart);
      const endIdx = differenceInCalendarDays(clampedEnd, monthStart);
      const left = startIdx * DAY_W;
      const width = (endIdx - startIdx + 1) * DAY_W;
      return { left, width, startIdx, endIdx };
    };

    // Assign non-overlapping lanes to events so they don't stack on top of each other
    const LANE_H = 30; // px per event lane
    const eventsWithLanes = (() => {
      const laneEnd = []; // last endIdx used per lane
      return monthEvents.map((ev) => {
        const { startIdx, endIdx } = getEventBand(ev);
        let lane = laneEnd.findIndex((e) => e < startIdx);
        if (lane === -1) { lane = laneEnd.length; laneEnd.push(endIdx); }
        else laneEnd[lane] = endIdx;
        return { ...ev, _lane: lane };
      });
    })();
    const numLanes = eventsWithLanes.length > 0
      ? Math.max(...eventsWithLanes.map((e) => e._lane + 1))
      : 0;
    const EVENT_ROW_H = numLanes * LANE_H + 8; // 8px padding top+bottom

    const today = new Date();
    const todayOffset = isSameMonth(today, ganttMonth)
      ? differenceInCalendarDays(today, monthStart)
      : -1;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

        {/* Month navigation header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
          <GanttChart size={16} className="text-blue-500 flex-shrink-0" />

          {/* Prev button */}
          <button
            onClick={() => setGanttMonth((m) => startOfMonth(addMonths(m, -1)))}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Month label — click to jump to current month */}
          <span className="text-sm font-semibold text-gray-800 min-w-[120px] text-center">
            {format(ganttMonth, "MMMM yyyy")}
          </span>

          {/* Next button */}
          <button
            onClick={() => setGanttMonth((m) => startOfMonth(addMonths(m, 1)))}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Jump to today */}
          {!isSameMonth(today, ganttMonth) && (
            <button
              onClick={() => setGanttMonth(startOfMonth(today))}
              className="ml-1 text-xs px-2.5 py-1 rounded-md border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors"
            >
              Today
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">
            {monthTasks.length} task{monthTasks.length !== 1 ? "s" : ""} this month
          </span>
        </div>

        <div className="overflow-x-auto">
          <div style={{ minWidth: NAME_W + days.length * DAY_W }}>

            {/* Day header row */}
            <div className="flex border-b border-gray-100 bg-gray-50">
              <div
                className="flex-shrink-0 border-r border-gray-100 px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide bg-gray-50 sticky left-0 z-20"
                style={{ width: NAME_W, minWidth: NAME_W }}
              >
                Task
              </div>
              <div className="flex">
                {days.map((day, i) => (
                  <div
                    key={i}
                    style={{ width: DAY_W, minWidth: DAY_W }}
                    className={`flex-shrink-0 text-center py-2.5 border-r border-gray-100 last:border-r-0 ${
                      isToday(day)
                        ? "bg-red-50 text-red-500 font-semibold"
                        : day.getDay() === 0 || day.getDay() === 6
                        ? "text-gray-300 bg-gray-50/80"
                        : "text-gray-400"
                    }`}
                  >
                    <div className="text-xs leading-tight font-medium">{format(day, "d")}</div>
                    <div className="text-[10px] leading-tight opacity-60">{format(day, "EEE")}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Events banner row — Style 3: transparent tint + left border + vertical name, lane-stacked */}
            {eventsWithLanes.length > 0 && (
              <div
                className="flex border-b border-gray-100"
                style={{ height: EVENT_ROW_H }}
              >
                <div
                  className="flex-shrink-0 border-r border-gray-100 px-3 flex items-center sticky left-0 bg-white z-10"
                  style={{ width: NAME_W, minWidth: NAME_W }}
                >
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm bg-purple-400 flex-shrink-0" />
                    <span className="text-[10px] font-semibold text-purple-500 uppercase tracking-wide">Events</span>
                  </div>
                </div>
                <div
                  className="relative flex-shrink-0"
                  style={{ width: days.length * DAY_W, height: EVENT_ROW_H }}
                >
                  {eventsWithLanes.map((ev, ei) => {
                    const { left, width } = getEventBand(ev);
                    const colors = [
                      { bg: "rgba(124,58,237,0.09)", border: "#7C3AED", text: "#5B21B6" },
                      { bg: "rgba(219,39,119,0.09)", border: "#DB2777", text: "#9D174D" },
                      { bg: "rgba(37,99,235,0.09)",  border: "#2563EB", text: "#1E3A8A" },
                      { bg: "rgba(5,150,105,0.09)",  border: "#059669", text: "#065F46" },
                      { bg: "rgba(234,88,12,0.09)",  border: "#EA580C", text: "#7C2D12" },
                      { bg: "rgba(168,85,247,0.09)", border: "#A855F7", text: "#581C87" },
                    ];
                    const c = colors[ei % colors.length];
                    const evStart = ev.date_start || ev.date;
                    const evEnd = ev.date_end || evStart;
                    const laneTop = 4 + ev._lane * LANE_H;
                    return (
                      <div
                        key={ev.id || ei}
                        className="absolute flex items-start overflow-hidden"
                        style={{
                          left,
                          top: laneTop,
                          width: Math.max(width, 4),
                          height: LANE_H - 2,
                          background: c.bg,
                          borderLeft: `3px solid ${c.border}`,
                          borderRadius: "0 3px 3px 0",
                          paddingTop: 3,
                          paddingLeft: 4,
                          zIndex: 2,
                        }}
                        title={`${ev.event_name}\n${evStart}${evEnd !== evStart ? ` → ${evEnd}` : ""}`}
                      >
                        <span
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            fontSize: 11,
                            fontWeight: 600,
                            color: c.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            lineHeight: 1.2,
                          }}
                        >
                          {ev.event_name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Task rows */}
            {monthTasks.length === 0 ? (
              <div className="px-6 py-10 text-center text-gray-400 text-sm">
                ไม่มี task ในเดือนนี้
              </div>
            ) : (
              monthTasks.map((task) => {
                const dueDate = task._barEnd || (task.date ? parseISO(task.date) : monthEnd);
                const startDate = task._barStart || (task.start_date ? parseISO(task.start_date) : dueDate);
                const dayIndex = differenceInCalendarDays(startDate, monthStart);
                const dueDayIndex = differenceInCalendarDays(dueDate, monthStart);
                // Clamp to visible portion of this month
                const clampedStart = Math.max(0, dayIndex);
                const clampedEnd = Math.min(days.length - 1, dueDayIndex);
                const visibleSpan = Math.max(1, clampedEnd - clampedStart + 1);
                const style = getGanttBarStyle(task);
                const assignees = getAssignees(task);
                const isDone = task.status === "done" || task.completed;

                return (
                  <div
                    key={task._key || task.id}
                    className="flex border-b border-gray-50 hover:bg-gray-50/40 transition-colors"
                    style={{ minHeight: 62 }}
                  >
                    {/* Sticky task name */}
                    <div
                      className="flex-shrink-0 border-r border-gray-100 px-3 flex items-center bg-white sticky left-0 z-10"
                      style={{ width: NAME_W, minWidth: NAME_W }}
                    >
                      <span
                        className={`text-sm leading-tight truncate ${
                          isDone ? "line-through text-gray-400" : "text-gray-700"
                        }`}
                        title={task.title}
                      >
                        {task.title}
                      </span>
                    </div>

                    {/* Timeline cells */}
                    <div
                      className="relative flex-shrink-0"
                      style={{ width: days.length * DAY_W, height: 62 }}
                    >
                      {/* Event background bands — Style 3 tint */}
                      {monthEvents.map((ev, ei) => {
                        const { left, width } = getEventBand(ev);
                        const tints   = ["rgba(124,58,237,0.05)", "rgba(219,39,119,0.05)", "rgba(37,99,235,0.05)", "rgba(5,150,105,0.05)"];
                        const borders = ["rgba(124,58,237,0.35)", "rgba(219,39,119,0.35)", "rgba(37,99,235,0.35)", "rgba(5,150,105,0.35)"];
                        return (
                          <div
                            key={ev.id || ei}
                            className="absolute top-0 bottom-0"
                            style={{
                              left,
                              width,
                              background: tints[ei % tints.length],
                              borderLeft: `3px solid ${borders[ei % borders.length]}`,
                            }}
                          />
                        );
                      })}

                      {/* Weekend shading */}
                      {days.map((day, i) =>
                        day.getDay() === 0 || day.getDay() === 6 ? (
                          <div
                            key={i}
                            className="absolute top-0 bottom-0 bg-gray-50/60"
                            style={{ left: i * DAY_W, width: DAY_W }}
                          />
                        ) : null
                      )}

                      {/* Today highlight */}
                      {todayOffset >= 0 && todayOffset < days.length && (
                        <div
                          className="absolute top-0 bottom-0 bg-red-50/60"
                          style={{ left: todayOffset * DAY_W, width: DAY_W }}
                        />
                      )}

                      {/* Task bar: spans start_date → due date (clamped to visible month) */}
                      {clampedStart < days.length && clampedEnd >= 0 && (
                        <div
                          className="absolute flex items-center gap-2 px-2.5 rounded-lg overflow-hidden cursor-pointer"
                          style={{
                            left: clampedStart * DAY_W + 2,
                            top: "50%",
                            transform: "translateY(-50%)",
                            height: 36,
                            width: Math.max(110, visibleSpan * DAY_W - 4),
                            background: style.done
                              ? `repeating-linear-gradient(135deg, ${style.bg}cc, ${style.bg}cc 6px, ${style.bg}99 6px, ${style.bg}99 12px)`
                              : style.bg,
                            opacity: style.done ? 0.75 : 1,
                          }}
                          onClick={() => handleEdit(task)}
                          onMouseEnter={(e) => setGanttTooltip({ task, x: e.clientX, y: e.clientY })}
                          onMouseMove={(e) => setGanttTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
                          onMouseLeave={() => setGanttTooltip(null)}
                        >
                          {/* Avatars (stacked) */}
                          {assignees.length > 0 && renderAvatarStack(assignees, {
                            size: 22,
                            max: 3,
                            borderColor: "rgba(255,255,255,0.7)",
                            fallbackBg: "rgba(255,255,255,0.25)",
                          })}
                          {task._recurring && (
                            <RefreshCw size={10} style={{ color: "rgba(255,255,255,0.85)", flexShrink: 0 }} />
                          )}
                          <span className="text-xs font-semibold truncate" style={{ color: "#fff" }}>
                            {task.title}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t border-gray-100 bg-white flex-wrap">
          <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Legend</span>
          {[
            { color: "#4F7EF7", label: "To Do" },
            { color: "#F97316", label: "In Progress" },
            { color: "#10B981", label: "Done" },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm" style={{ background: color }} />
              <span className="text-xs text-gray-500">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-1">
            <div className="w-3 h-3 rounded-sm" style={{ background: "rgba(124,58,237,0.15)", borderLeft: "3px solid #7C3AED" }} />
            <span className="text-xs text-gray-500">Event</span>
          </div>
          <div className="flex items-center gap-1.5 ml-1">
            <div className="w-3 h-3 rounded-sm bg-red-50 border border-red-100" />
            <span className="text-xs text-gray-500">Today</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gray-50 border border-gray-100" />
            <span className="text-xs text-gray-500">Weekend</span>
          </div>
        </div>
      </div>
    );
  };

  // ── Calendar sub-mode switcher header ────────────────────────
  const renderCalSubHeader = (navLeft, label, navRight, todayBtn) => (
    <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
      <CalendarDays size={16} className="text-blue-500 flex-shrink-0" />
      {navLeft}
      <span className="text-sm font-semibold text-gray-800 min-w-[160px] text-center">{label}</span>
      {navRight}
      {todayBtn}
      <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        {["month", "week"].map((m) => (
          <button
            key={m}
            onClick={() => setCalSubMode(m)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors capitalize ${
              calSubMode === m ? "bg-blue-500 text-white" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {m === "month" ? "Month" : "Week"}
          </button>
        ))}
      </div>
    </div>
  );

  const navArrow = (dir, onClick) => (
    <button
      onClick={onClick}
      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        {dir === "left"
          ? <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          : <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        }
      </svg>
    </button>
  );

  // ── Monthly Grid Calendar (spanning bars) ────────────────────
  const renderCalendarMonthView = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const allDays = eachDayOfInterval({ start: gridStart, end: gridEnd });
    const numWeeks = allDays.length / 7;
    const today = new Date();

    // Build weeks array: weeks[wi][ci] = Date object (local midnight via addDays)
    const weeks = Array.from({ length: numWeeks }, (_, wi) =>
      Array.from({ length: 7 }, (_, ci) => addDays(gridStart, wi * 7 + ci))
    );

    // Pre-compute each day's YYYY-MM-DD string to avoid timezone mismatch
    // (parseISO treats date-only strings as UTC; addDays works in local time)
    const weekDateStrs = weeks.map((weekDays) =>
      weekDays.map((d) => format(d, "yyyy-MM-dd"))
    );

    // ── Event pills (Style C) ─────────────────────────────────────
    const EVENT_PILL_COLORS = [
      { bg: "#ede9fe", text: "#5b21b6" }, // purple
      { bg: "#fce7f3", text: "#9d174d" }, // pink
      { bg: "#dbeafe", text: "#1e3a8a" }, // blue
      { bg: "#d1fae5", text: "#065f46" }, // green
      { bg: "#ffedd5", text: "#c2410c" }, // orange
      { bg: "#fef3c7", text: "#92400e" }, // amber
    ];
    const gridStartStr = format(gridStart, "yyyy-MM-dd");
    const gridEndStr   = format(gridEnd,   "yyyy-MM-dd");
    const visibleMonthEvents = events
      .filter((ev) => {
        const s = ev.date_start || ev.date;
        const e = ev.date_end   || s;
        return s && e >= gridStartStr && s <= gridEndStr;
      })
      .sort((a, b) =>
        (a.date_start || a.date || "").localeCompare(b.date_start || b.date || "")
      );
    // Map dateStr → [{ev, colorIdx}]
    const eventsPerDay = {};
    visibleMonthEvents.forEach((ev, idx) => {
      const s = ev.date_start || ev.date;
      const e = ev.date_end   || s;
      allDays.forEach((day) => {
        const ds = format(day, "yyyy-MM-dd");
        if (ds >= s && ds <= e) {
          if (!eventsPerDay[ds]) eventsPerDay[ds] = [];
          eventsPerDay[ds].push({ ev, colorIdx: idx % EVENT_PILL_COLORS.length });
        }
      });
    });

    // Build segments using string comparison — immune to timezone issues
    const allSegments = [];
    for (const task of filteredTasks) {
      if (!task.date) continue;
      const taskEndStr   = task.date;                          // "YYYY-MM-DD"
      const taskStartStr = task.start_date || taskEndStr;      // "YYYY-MM-DD"

      weeks.forEach((_, wi) => {
        const dayStrs    = weekDateStrs[wi];
        const weekStartS = dayStrs[0];
        const weekEndS   = dayStrs[6];

        // Skip weeks that don't overlap at all
        if (taskEndStr < weekStartS || taskStartStr > weekEndS) return;

        // First column where dayStr >= taskStartStr
        let colStart = 0;
        for (let ci = 0; ci < 7; ci++) {
          if (dayStrs[ci] >= taskStartStr) { colStart = ci; break; }
        }
        // Last column where dayStr <= taskEndStr
        let colEnd = 6;
        for (let ci = 6; ci >= 0; ci--) {
          if (dayStrs[ci] <= taskEndStr) { colEnd = ci; break; }
        }

        const isStart = taskStartStr >= weekStartS;
        const isEnd   = taskEndStr   <= weekEndS;
        allSegments.push({ task, week: wi, colStart, colEnd, isStart, isEnd });
      });
    }

    // Group by week and assign non-overlapping vertical slots
    const segsByWeek = Array.from({ length: numWeeks }, () => []);
    allSegments.forEach((s) => segsByWeek[s.week].push(s));
    segsByWeek.forEach((weekSegs) => {
      // Sort: earlier start first, then longer span first for stable layout
      weekSegs.sort((a, b) => a.colStart - b.colStart || (b.colEnd - b.colStart) - (a.colEnd - a.colStart));
      weekSegs.forEach((s) => {
        let slot = 0;
        while (weekSegs.some((p) => p !== s && p.slot === slot && p.slot !== undefined && p.colEnd >= s.colStart && p.colStart <= s.colEnd)) slot++;
        s.slot = slot;
      });
    });

    const COL_PCT = 100 / 7;
    const MAX_SLOTS = 3; // max visible bars per week row
    const SLOT_H = 24;  // px per event row
    const BAR_H = 20;   // px bar height
    const BAR_GAP = 3;  // px gap above first bar

    const getBarColors = (task) => {
      const st = task.status || (task.completed ? "done" : "todo");
      if (st === "done")        return { bg: "#d1fae5", text: "#065f46", dot: "#10b981" };
      if (st === "in_progress") return { bg: "#ffedd5", text: "#c2410c", dot: "#f97316" };
      return                           { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" };
    };

    const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const todayBtn = !isSameMonth(today, calendarMonth) ? (
      <button
        onClick={() => setCalendarMonth(startOfMonth(today))}
        className="ml-1 text-xs px-2.5 py-1 rounded-md border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors flex-shrink-0"
      >
        Today
      </button>
    ) : null;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {renderCalSubHeader(
          navArrow("left", () => setCalendarMonth((m) => startOfMonth(addMonths(m, -1)))),
          format(calendarMonth, "MMMM yyyy"),
          navArrow("right", () => setCalendarMonth((m) => startOfMonth(addMonths(m, 1)))),
          todayBtn
        )}

        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
          {WEEKDAY_LABELS.map((d) => (
            <div key={d} className="py-2 text-center text-xs font-medium text-gray-400 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Weeks: date-number row + spanning-bar row */}
        {weeks.map((weekDays, wi) => {
          const weekSegs = segsByWeek[wi];
          const visibleSegs = weekSegs.filter((s) => s.slot < MAX_SLOTS);
          const hiddenCount = weekSegs.filter((s) => s.slot >= MAX_SLOTS).length;
          const visibleSlots = visibleSegs.length > 0 ? Math.max(...visibleSegs.map((s) => s.slot)) + 1 : 0;
          const evRowH = BAR_GAP + visibleSlots * SLOT_H + (hiddenCount > 0 ? 16 : 4);

          return (
            <div key={wi}>
              {/* Date number row */}
              <div className="grid grid-cols-7">
                {weekDays.map((day, ci) => {
                  const inMonth   = isSameMonth(day, calendarMonth);
                  const todayDay  = isToday(day);
                  const isWeekend = ci >= 5;
                  const dayEvs    = eventsPerDay[weekDateStrs[wi][ci]] || [];
                  const showEvs   = dayEvs.slice(0, 2);
                  const extraEvs  = dayEvs.length - showEvs.length;
                  return (
                    <div
                      key={ci}
                      className={`border-r border-gray-100 last:border-r-0 px-1.5 pt-1.5 pb-1.5 ${
                        !inMonth ? "bg-gray-50/60" : todayDay ? "bg-blue-50/30" : ""
                      }`}
                    >
                      <span
                        className={`text-xs font-semibold leading-none inline-flex items-center justify-center ${
                          todayDay
                            ? "w-5 h-5 bg-blue-500 text-white rounded-full"
                            : !inMonth
                            ? "text-gray-300"
                            : isWeekend
                            ? "text-gray-400"
                            : "text-gray-700"
                        }`}
                      >
                        {format(day, "d")}
                      </span>

                      {/* Event pills */}
                      {showEvs.map(({ ev, colorIdx }) => {
                        const c = EVENT_PILL_COLORS[colorIdx];
                        return (
                          <div
                            key={ev.id}
                            title={ev.event_name}
                            className="mt-0.5 truncate leading-tight rounded-full"
                            style={{
                              background: c.bg,
                              color: c.text,
                              fontSize: 9,
                              fontWeight: 600,
                              padding: "1px 5px",
                              maxWidth: "100%",
                            }}
                          >
                            {ev.event_name}
                          </div>
                        );
                      })}
                      {extraEvs > 0 && (
                        <div style={{ fontSize: 9, color: "#9ca3af", paddingLeft: 2, marginTop: 1 }}>
                          +{extraEvs} more
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Event bars row */}
              <div
                className="relative border-b border-gray-100"
                style={{ height: Math.max(evRowH, 8) }}
              >
                {/* Day background strips */}
                {weekDays.map((day, ci) => {
                  const inMonth = isSameMonth(day, calendarMonth);
                  const todayDay = isToday(day);
                  return (
                    <div
                      key={ci}
                      className="absolute top-0 bottom-0"
                      style={{
                        left: `${ci * COL_PCT}%`,
                        width: `${COL_PCT}%`,
                        background: todayDay
                          ? "rgba(239,246,255,0.6)"
                          : !inMonth
                          ? "rgba(249,250,251,0.9)"
                          : "transparent",
                        borderRight: ci < 6 ? "0.5px solid #f3f4f6" : "none",
                      }}
                    />
                  );
                })}

                {/* Spanning task bars — capped at MAX_SLOTS */}
                {visibleSegs.map((s) => {
                  const colors = getBarColors(s.task);
                  const segAssignees = getAssignees(s.task);
                  const left = s.colStart * COL_PCT;
                  const width = (s.colEnd - s.colStart + 1) * COL_PCT;
                  const top = BAR_GAP + s.slot * SLOT_H;
                  const radius = s.isStart && s.isEnd ? "5px"
                    : s.isStart ? "5px 0 0 5px"
                    : s.isEnd   ? "0 5px 5px 0"
                    : "0";
                  const isDone = s.task.status === "done" || s.task.completed;

                  return (
                    <div
                      key={`${s.task.id}-${s.week}`}
                      onClick={() => handleEdit(s.task)}
                      title={`${s.task.title}${s.task.assigned_to ? ` — ${s.task.assigned_to}` : ""}`}
                      className="absolute flex items-center gap-1.5 cursor-pointer overflow-hidden hover:opacity-75 transition-opacity"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        top,
                        height: BAR_H,
                        background: colors.bg,
                        color: colors.text,
                        borderRadius: radius,
                        paddingLeft: s.isStart ? 6 : 3,
                        paddingRight: 5,
                        fontSize: 11,
                        fontWeight: 500,
                        zIndex: 2,
                      }}
                    >
                      {s.isStart && (
                        <>
                          {segAssignees.length > 0 && renderAvatarStack(segAssignees, {
                            size: 14,
                            max: 2,
                            borderColor: "white",
                            fallbackBg: colors.dot,
                          })}
                          <span className={`truncate ${isDone ? "line-through opacity-60" : ""}`}>
                            {s.task.title}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}

                {/* "+N more" indicator when tasks overflow MAX_SLOTS */}
                {hiddenCount > 0 && (
                  <div
                    className="absolute text-[10px] font-medium text-gray-400"
                    style={{
                      bottom: 2,
                      left: 6,
                      zIndex: 3,
                    }}
                  >
                    +{hiddenCount} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Weekly Timeline Calendar ──────────────────────────────────
  const renderCalendarWeekView = () => {
    const weekStart = startOfWeek(calendarWeek, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const weekEnd = addDays(weekStart, 6);
    const today = new Date();

    const tasksByDate = {};
    for (const t of filteredTasks) {
      if (t.date) {
        if (!tasksByDate[t.date]) tasksByDate[t.date] = [];
        tasksByDate[t.date].push(t);
      }
    }

    const isThisWeek = days.some((d) => isToday(d));
    const todayBtn = !isThisWeek ? (
      <button
        onClick={() => setCalendarWeek(startOfWeek(today, { weekStartsOn: 1 }))}
        className="ml-1 text-xs px-2.5 py-1 rounded-md border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors flex-shrink-0"
      >
        Today
      </button>
    ) : null;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {renderCalSubHeader(
          navArrow("left", () => setCalendarWeek((w) => addDays(w, -7))),
          `${format(weekStart, "d MMM")} — ${format(weekEnd, "d MMM yyyy")}`,
          navArrow("right", () => setCalendarWeek((w) => addDays(w, 7))),
          todayBtn
        )}

        {/* 7-column week grid */}
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
        <div className="grid grid-cols-7 divide-x divide-gray-100 min-w-[640px]">
          {days.map((day) => {
            const dateStr = format(day, "yyyy-MM-dd");
            const dayTasks = (tasksByDate[dateStr] || []).sort((a, b) => {
              const order = { high: 0, mid: 1, low: 2 };
              return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
            });
            const todayDay = isToday(day);

            return (
              <div key={dateStr} className={`min-h-[380px] flex flex-col ${todayDay ? "bg-blue-50/20" : ""}`}>
                {/* Day header */}
                <div className={`px-2 py-2.5 border-b border-gray-100 text-center ${todayDay ? "bg-blue-50" : "bg-gray-50"}`}>
                  <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{format(day, "EEE")}</div>
                  <div className={`text-xl font-semibold mt-0.5 ${todayDay ? "text-blue-500" : "text-gray-800"}`}>
                    {format(day, "d")}
                  </div>
                  {dayTasks.length > 0 && (
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {dayTasks.length} task{dayTasks.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>

                {/* Tasks */}
                <div className="p-1.5 space-y-1.5 flex-1">
                  {dayTasks.map((task) => {
                    const weekAssignees = getAssignees(task);
                    const barColor =
                      task.status === "done" ? "#10B981" :
                      task.status === "in_progress" ? "#F97316" : "#4F7EF7";
                    const isDone = task.status === "done" || task.completed;

                    return (
                      <div
                        key={task.id}
                        onClick={() => handleEdit(task)}
                        className="cursor-pointer rounded-lg p-2 bg-white hover:bg-gray-50 border border-gray-100 transition-colors"
                        style={{ borderLeft: `3px solid ${barColor}` }}
                      >
                        <p className={`text-[11px] font-medium leading-tight mb-1.5 ${isDone ? "line-through text-gray-400" : "text-gray-800"}`}>
                          {task.title}
                        </p>
                        {weekAssignees.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            {renderAvatarStack(weekAssignees, { size: 18, max: 3, borderColor: "white", fallbackBg: barColor })}
                            <span className="text-[10px] text-gray-500 truncate flex-1">
                              {weekAssignees.slice(0, 2).join(", ")}{weekAssignees.length > 2 ? ` +${weekAssignees.length - 2}` : ""}
                            </span>
                          </div>
                        )}
                        {task.priority === "high" && (
                          <div className="mt-1">
                            <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-600 font-medium">
                              High
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
    );
  };

  // ── My Tasks View (dark theme) ───────────────────────────────
  const renderMyTasksView = () => {
    const today = new Date();
    const todayStr = format(today, "yyyy-MM-dd");
    // Case-insensitive match — handles 'Plai' vs 'plai' mismatch between AuthContext and Firestore staff names
    const myName = user?.name?.toLowerCase() ?? '';
    const myTasks = tasks.filter((t) =>
      user && getAssignees(t).some((n) => n.toLowerCase() === myName)
    );

    const pOrder = { high: 0, mid: 1, low: 2 };
    const todoTasks = myTasks
      .filter((t) => t.status === "todo" && !t.completed)
      .sort((a, b) => {
        const pd = (pOrder[a.priority] ?? 1) - (pOrder[b.priority] ?? 1);
        return pd !== 0 ? pd : (a.date ?? "").localeCompare(b.date ?? "");
      });
    const doingTasks = myTasks
      .filter((t) => t.status === "in_progress" && !t.completed)
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
    const doneTasks = myTasks
      .filter((t) => t.status === "done" || t.completed)
      .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
      .slice(0, 12);
    const overdueTasks = myTasks
      .filter((t) => t.date && t.date < todayStr && t.status !== "done" && !t.completed)
      .sort((a, b) => b.date.localeCompare(a.date));

    const getGreeting = () => {
      const h = new Date().getHours();
      if (h < 12) return "Good Morning";
      if (h < 17) return "Good Afternoon";
      return "Good Evening";
    };

    const priorityLight = {
      high: { label: "High", color: "#dc2626", bg: "#fee2e2", bar: "#ef4444" },
      mid:  { label: "Mid",  color: "#d97706", bg: "#fef3c7", bar: "#f59e0b" },
      low:  { label: "Low",  color: "#059669", bg: "#d1fae5", bar: "#10b981" },
    };

    const renderKanbanCard = (task) => {
      const pri = priorityLight[task.priority] || priorityLight.mid;
      const isDone = task.status === "done" || task.completed;
      const isOverdue = task.date && task.date < todayStr && !isDone;
      const borderColor = isDone ? "#10b981" : pri.bar;
      return (
        <div
          key={task.id}
          onClick={() => handleEdit(task)}
          className="p-3.5 cursor-pointer transition-shadow hover:shadow-md active:scale-[0.99]"
          style={{
            background: isDone ? "#ecfdf5" : "#fff",
            border: isDone ? "0.5px solid #a7f3d0" : "0.5px solid #e5e7eb",
            borderLeft: `3px solid ${borderColor}`,
            borderRadius: "0 12px 12px 0",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <p
            className="text-[13px] font-semibold leading-snug mb-2.5"
            style={{
              color: isDone ? "#94a3b8" : "#1e293b",
              textDecoration: isDone ? "line-through" : "none",
            }}
          >
            {task.title}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: pri.bg, color: pri.color }}
            >
              {pri.label}
            </span>
            {task.date && (
              <span
                className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full"
                style={{
                  background: isOverdue ? "#fee2e2" : "#f1f5f9",
                  color: isOverdue ? "#dc2626" : "#64748b",
                }}
              >
                <Calendar size={9} />
                {format(parseISO(task.date), "d MMM yyyy")}
              </span>
            )}
            {task.tag && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: "#f1f5f9", color: "#64748b" }}
              >
                {getTagLabel(task.tag)}
              </span>
            )}
          </div>
          {getAssignees(task).length > 1 && (
            <div className="mt-2 flex items-center gap-1">
              {renderAvatarStack(getAssignees(task), { size: 18, max: 3, borderColor: "#fff", fallbackBg: "#3b6ef5" })}
            </div>
          )}
        </div>
      );
    };

    const columns = [
      { key: "todo",        label: "To do", color: "#3b6ef5", bg: "#e6edfd", tasks: todoTasks },
      { key: "in_progress", label: "Doing", color: "#d97706", bg: "#fef3c7", tasks: doingTasks },
      { key: "done",        label: "Done",  color: "#059669", bg: "#d1fae5", tasks: doneTasks },
    ];

    return (
      <div className="space-y-5">

        {/* ── Profile header ── */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm" style={{ color: "#94a3b8" }}>{getGreeting()}</p>
            <h2 className="text-2xl font-bold" style={{ color: "#1e293b" }}>Hi, {user?.name || "User"}</h2>
          </div>
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center text-base font-black text-white"
            style={{ background: "#3b6ef5" }}
          >
            {user?.name?.[0] || "U"}
          </div>
        </div>

        {/* ── Stats row + Add button ── */}
        <div className="flex items-center justify-between">
          <div className="flex gap-6">
            {columns.map(({ key, label, color, tasks: t }) => (
              <div key={key} className="text-center">
                <p className="text-xl font-black leading-none" style={{ color }}>{t.length}</p>
                <p className="text-[11px] font-medium mt-0.5" style={{ color: "#94a3b8" }}>{label}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: "#3b6ef5" }}
          >
            <Plus size={15} /> Add task
          </button>
        </div>

        {/* ── Overdue alert ── */}
        {overdueTasks.length > 0 && (
          <div
            className="flex items-center gap-3 rounded-2xl px-4 py-3"
            style={{ background: "#fef2f2", border: "0.5px solid #fecaca" }}
          >
            <AlertCircle size={16} style={{ color: "#ef4444", flexShrink: 0 }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: "#dc2626" }}>
                {overdueTasks.length} overdue task{overdueTasks.length > 1 ? "s" : ""}
              </p>
              <p className="text-xs truncate" style={{ color: "#ef4444" }}>
                {overdueTasks.slice(0, 2).map((t) => t.title).join(", ")}
                {overdueTasks.length > 2 ? ` +${overdueTasks.length - 2} more` : ""}
              </p>
            </div>
          </div>
        )}

        {/* ── Kanban columns ── */}
        {myTasks.length === 0 ? (
          <div
            className="rounded-2xl py-14 flex flex-col items-center gap-3"
            style={{ background: "#f8fafc", border: "0.5px solid #e5e7eb" }}
          >
            <CheckCheck size={36} style={{ color: "#cbd5e1" }} />
            <p className="text-sm font-medium" style={{ color: "#94a3b8" }}>
              No tasks assigned to you yet
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {columns.map(({ key, label, color, bg, tasks: colTasks }) => (
              <div key={key} className="rounded-2xl p-3" style={{ background: "#f4f5f7" }}>
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
                  <span className="text-sm font-bold" style={{ color: "#1e293b" }}>{label}</span>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
                    style={{ background: bg, color }}
                  >
                    {colTasks.length}
                  </span>
                </div>
                <div className="space-y-2.5">
                  {colTasks.map(renderKanbanCard)}
                  {colTasks.length === 0 && (
                    <div
                      className="py-8 rounded-xl flex items-center justify-center"
                      style={{ border: "1px dashed #d1d5db", background: "rgba(255,255,255,0.4)" }}
                    >
                      <p className="text-xs" style={{ color: "#cbd5e1" }}>Empty</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container" data-testid="tasks-loading">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const priorityLeftBorder = (priority) => {
    if (priority === "high") return "border-l-red-400";
    if (priority === "low") return "border-l-emerald-400";
    return "border-l-amber-300";
  };

  const renderTaskRow = (task) => (
    <div
      key={task.id}
      className={`flex items-center gap-4 px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 border-l-4 ${
        task.status === "done" ? "border-l-emerald-300 bg-gray-50/40" : priorityLeftBorder(task.priority)
      }`}
      data-testid={`task-row-${task.id}`}
    >
      {/* Checkbox */}
      <Checkbox
        checked={task.status === "done" || task.completed}
        onCheckedChange={() => toggleTaskComplete(task)}
        className="border-gray-300 data-[state=checked]:bg-[#BFFF00] data-[state=checked]:border-[#BFFF00]"
        data-testid={`task-checkbox-${task.id}`}
      />
      
      {/* Staff avatars */}
      <div className="w-[100px]">
        {(() => {
          const listAssignees = getAssignees(task);
          return listAssignees.length > 0 ? (
            <div className="flex items-center gap-1.5">
              {renderAvatarStack(listAssignees, { size: 22, max: 3, borderColor: "white", fallbackBg: "#3B82F6" })}
              <span className="text-xs text-gray-600 truncate">
                {listAssignees.length === 1 ? listAssignees[0] : `${listAssignees.length} staff`}
              </span>
            </div>
          ) : (
            <span className="text-gray-300 text-sm">-</span>
          );
        })()}
      </div>

      {/* Task Name */}
      <div className="flex-1 min-w-[200px] flex items-center gap-2">
        <span className={`text-sm font-medium ${task.status === "done" ? "line-through text-gray-400" : "text-gray-900"}`}>
          {task.title}
        </span>
        {task.recurrence?.type && task.recurrence.type !== "none" && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-500 border border-blue-100 flex-shrink-0">
            <RefreshCw size={9} />
            {getRecurrenceLabel(task.recurrence)}
          </span>
        )}
      </div>
      
      {/* Due Date */}
      <div className="w-[120px] flex items-center gap-2 text-sm text-gray-500">
        <Calendar size={14} className="text-blue-500" />
        {task.date ? (
          <span className={isToday(parseISO(task.date)) ? "text-[#BFFF00] font-medium" : ""}>
            {format(parseISO(task.date), "d MMM yyyy")}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </div>
      
      {/* Task Tag */}
      <div className="w-[100px]">
        {task.tag ? (
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getTagStyle(task.tag)}`}>
            {getTagLabel(task.tag)}
          </span>
        ) : (
          <span className="text-gray-300 text-sm">-</span>
        )}
      </div>
      
      {/* Priority */}
      <div className="w-[80px]">
        {task.priority ? (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getPriorityStyle(task.priority)}`}>
            <Flag size={10} />
            {getPriorityLabel(task.priority)}
          </span>
        ) : (
          <span className="text-gray-300 text-sm">-</span>
        )}
      </div>
      
      {/* Actions */}
      <div className="w-[60px] flex justify-end gap-1">
        <button
          onClick={() => handleEdit(task)}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={() => handleDelete(task.id)}
          className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );

  const renderSection = (statusKey, label, taskList) => (
    <div className="mb-4" data-testid={`section-${statusKey}`}>
      {/* Section Header */}
      <button
        onClick={() => toggleSection(statusKey)}
        className="flex items-center gap-2 w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
      >
        {expandedSections[statusKey] ? (
          <ChevronDown size={18} className="text-gray-500" />
        ) : (
          <ChevronRight size={18} className="text-gray-500" />
        )}
        <span className="font-semibold text-gray-900">{label}</span>
        <span className="text-sm text-gray-400 ml-2">({taskList.length})</span>
        <div className="flex-1" />
        <MoreVertical size={16} className="text-gray-400" />
      </button>
      
      {/* Section Content */}
      {expandedSections[statusKey] && taskList.length > 0 && (
        <div className="mt-2 bg-white rounded-lg border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500 uppercase">
            <div className="w-6" /> {/* Checkbox space */}
            <div className="w-[100px]">Staff</div>
            <div className="flex-1 min-w-[200px]">Task</div>
            <div className="w-[120px]">Due Date</div>
            <div className="w-[100px]">Task Tags</div>
            <div className="w-[80px]">Priority</div>
            <div className="w-[60px]" /> {/* Actions space */}
          </div>
          
          {/* Task Rows */}
          {taskList.map(task => renderTaskRow(task))}
        </div>
      )}
      
      {expandedSections[statusKey] && taskList.length === 0 && (
        <div className="mt-2 p-6 bg-white rounded-lg border border-gray-100 text-center text-gray-400 text-sm">
          No tasks in this section
        </div>
      )}
    </div>
  );

  return (
    <div className="page-container" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Tasks</h1>
          <p className="text-gray-500 mt-1">Manage your daily tasks and priorities</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Staff Filter — hidden in My Tasks view */}
          {viewMode !== "mytasks" && (
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-[150px] border-gray-200 bg-white">
                <User size={16} className="mr-2 text-gray-400" />
                <SelectValue placeholder="All Staff" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">All Staff</SelectItem>
                {(() => {
                  const fromFirestore = [...new Map(staff.filter(s => s.name).map(s => [s.name, s])).values()];
                  const firestoreNamesLower = new Set(fromFirestore.map(s => s.name?.toLowerCase()));
                  const fallback = Object.entries(STAFF_NAME_TO_USER_ID)
                    .filter(([name]) => !firestoreNamesLower.has(name.toLowerCase()))
                    .map(([name, id]) => ({ id, name }));
                  return [...fromFirestore, ...fallback].map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          )}

          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "list" ? "bg-blue-500 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <LayoutList size={16} />
              <span>List</span>
            </button>
            <button
              onClick={() => setViewMode("board")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "board" ? "bg-blue-500 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <LayoutGrid size={16} />
              <span>Board</span>
            </button>
            <button
              onClick={() => setViewMode("gantt")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "gantt" ? "bg-blue-500 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <GanttChart size={16} />
              <span>Gantt</span>
            </button>
            <button
              onClick={() => setViewMode("calendar")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "calendar" ? "bg-blue-500 text-white" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <CalendarDays size={16} />
              <span>Calendar</span>
            </button>
            <button
              onClick={() => setViewMode("mytasks")}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === "mytasks"
                  ? "text-black font-bold"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              style={viewMode === "mytasks" ? { background: "#BFFF00" } : {}}
            >
              <User size={16} />
              <span>My</span>
            </button>
          </div>
          
          <ExportDialog
            title="Export Tasks"
            filename="tasks"
            data={exportData}
            columns={TASK_EXPORT_COLUMNS}
            recordLabel="tasks"
          >
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">From Date</Label>
                  <Input type="date" value={exportFilters.dateFrom} onChange={(e) => setExportFilters((f) => ({ ...f, dateFrom: e.target.value }))} className="border-gray-200" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">To Date</Label>
                  <Input type="date" value={exportFilters.dateTo} onChange={(e) => setExportFilters((f) => ({ ...f, dateTo: e.target.value }))} className="border-gray-200" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700 text-sm">Status</Label>
                <Select value={exportFilters.status} onValueChange={(v) => setExportFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all">All statuses</SelectItem>
                    {statuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">Priority</Label>
                  <Select value={exportFilters.priority} onValueChange={(v) => setExportFilters((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="all">All</SelectItem>
                      {priorities.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-gray-700 text-sm">Tag</Label>
                  <Select value={exportFilters.tag} onValueChange={(v) => setExportFilters((f) => ({ ...f, tag: v }))}>
                    <SelectTrigger className="border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="all">All</SelectItem>
                      {tags.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </ExportDialog>

          {/* Add Task Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-task-btn"
                className="bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg"
                onClick={() => resetForm()}
              >
                <Plus size={18} />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900">
                  {editingTask ? "Edit Task" : "Create New Task"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Task Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Task title"
                    required
                    className="border-gray-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Task description..."
                    className="border-gray-200"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Due Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="border-gray-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Assigned To</Label>
                  <MultiStaffSelect
                    value={formData.assigned_to}
                    onChange={(v) => setFormData({ ...formData, assigned_to: v })}
                    options={(() => {
                      const fromFirestore = [...new Map(staff.filter(s => s.id && s.name).map(s => [s.name, s])).values()];
                      const firestoreNamesLower = new Set(fromFirestore.map(s => s.name?.toLowerCase()));
                      const fallback = Object.entries(STAFF_NAME_TO_USER_ID)
                        .filter(([name]) => !firestoreNamesLower.has(name.toLowerCase()))
                        .map(([name, id]) => ({ id, name }));
                      return [...fromFirestore, ...fallback];
                    })()}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Task Tag</Label>
                    <Select
                      value={formData.tag}
                      onValueChange={(v) => setFormData({ ...formData, tag: v })}
                    >
                      <SelectTrigger className="border-gray-200">
                        <SelectValue placeholder="Select tag" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {tags.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => setFormData({ ...formData, priority: v })}
                    >
                      <SelectTrigger className="border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {priorities.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v, completed: v === "done" })}
                  >
                    <SelectTrigger className="border-gray-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {statuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Task location"
                    className="border-gray-200"
                  />
                </div>

                {/* Recurrence */}
                <div className="p-4 bg-blue-50/60 rounded-lg space-y-3 border border-blue-100">
                  <div className="flex items-center gap-2">
                    <RefreshCw size={14} className="text-blue-500" />
                    <Label className="text-gray-700 font-medium">ทำซ้ำ (Recurrence)</Label>
                  </div>
                  <Select
                    value={formData.recurrence?.type || "none"}
                    onValueChange={(v) =>
                      setFormData({ ...formData, recurrence: { type: v, days: [], date: 1, start: 1, end: 5 } })
                    }
                  >
                    <SelectTrigger className="border-blue-200 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="none">ไม่มี</SelectItem>
                      <SelectItem value="daily">ทุกวัน</SelectItem>
                      <SelectItem value="weekly">ทุกสัปดาห์ (เลือกวัน)</SelectItem>
                      <SelectItem value="monthly">ทุกเดือน — วันที่ X</SelectItem>
                      <SelectItem value="monthly_range">ทุกเดือน — วันที่ X ถึง Y</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Weekly: day picker */}
                  {formData.recurrence?.type === "weekly" && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-500">เลือกวันที่ทำซ้ำ</p>
                      <div className="flex gap-1.5">
                        {WEEKDAYS_TH.map((day, i) => {
                          const selected = (formData.recurrence?.days || []).includes(i);
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => {
                                const cur = formData.recurrence?.days || [];
                                const next = selected ? cur.filter((d) => d !== i) : [...cur, i].sort();
                                setFormData({ ...formData, recurrence: { ...formData.recurrence, days: next } });
                              }}
                              className={`w-9 h-9 rounded-full text-xs font-semibold border transition-colors ${
                                selected
                                  ? "bg-blue-500 text-white border-blue-500"
                                  : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                              }`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Monthly: specific date */}
                  {formData.recurrence?.type === "monthly" && (
                    <div className="flex items-center gap-2">
                      <Label className="text-gray-600 text-sm whitespace-nowrap">วันที่</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.recurrence?.date || 1}
                        onChange={(e) =>
                          setFormData({ ...formData, recurrence: { ...formData.recurrence, date: parseInt(e.target.value) || 1 } })
                        }
                        className="border-gray-200 w-20 bg-white"
                      />
                      <span className="text-sm text-gray-500">ของทุกเดือน</span>
                    </div>
                  )}

                  {/* Monthly range */}
                  {formData.recurrence?.type === "monthly_range" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-gray-600 text-sm whitespace-nowrap">วันที่</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.recurrence?.start || 1}
                        onChange={(e) =>
                          setFormData({ ...formData, recurrence: { ...formData.recurrence, start: parseInt(e.target.value) || 1 } })
                        }
                        className="border-gray-200 w-20 bg-white"
                      />
                      <span className="text-sm text-gray-400">ถึง</span>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.recurrence?.end || 5}
                        onChange={(e) =>
                          setFormData({ ...formData, recurrence: { ...formData.recurrence, end: parseInt(e.target.value) || 5 } })
                        }
                        className="border-gray-200 w-20 bg-white"
                      />
                      <span className="text-sm text-gray-500">ของทุกเดือน</span>
                    </div>
                  )}

                  {/* Preview label */}
                  {formData.recurrence?.type !== "none" && (
                    <p className="text-xs text-blue-600 font-medium">
                      ↻ {getRecurrenceLabel(formData.recurrence)}
                    </p>
                  )}
                </div>

                {/* Transportation Cost */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <Label className="text-gray-700 font-medium">Transportation</Label>
                  <div className="space-y-2">
                    <Label className="text-gray-600 text-sm">Cost (฿)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.transportation_cost}
                      onChange={(e) => setFormData({ ...formData, transportation_cost: parseFloat(e.target.value) || 0 })}
                      className="border-gray-200 bg-white"
                    />
                  </div>

                  {/* Receipt Upload */}
                  <div className="space-y-2">
                    <Label className="text-gray-600 text-sm">Receipt</Label>
                    {formData.receipt_url ? (
                      <div className="relative">
                        <a 
                          href={formData.receipt_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <span className="text-sm text-gray-700">View Receipt</span>
                        </a>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, receipt_url: "" })}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors bg-white">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleReceiptUpload}
                          className="hidden"
                          disabled={uploading}
                        />
                        {uploading ? (
                          <div className="w-6 h-6 border-2 border-[#BFFF00] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">Upload receipt</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1 border-gray-200">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white">
                    {editingTask ? "Update" : "Create Task"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Stats Bar */}
      {viewMode !== "mytasks" && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: "ทั้งหมด", count: filteredTasks.length, color: "text-gray-700", bg: "bg-white border border-gray-200", dot: null },
            { label: "To Do", count: tasksByStatus.todo.length, color: "text-blue-600", bg: "bg-blue-50 border border-blue-100", dot: "#4F7EF7" },
            { label: "In Progress", count: tasksByStatus.in_progress.length, color: "text-amber-600", bg: "bg-amber-50 border border-amber-100", dot: "#F97316" },
            { label: "Done", count: tasksByStatus.done.length, color: "text-emerald-600", bg: "bg-emerald-50 border border-emerald-100", dot: "#10B981" },
            { label: "Overdue", count: overdueCount, color: overdueCount > 0 ? "text-red-600" : "text-gray-400", bg: overdueCount > 0 ? "bg-red-50 border border-red-200" : "bg-white border border-gray-200", dot: overdueCount > 0 ? "#ef4444" : null },
          ].map(({ label, count, color, bg, dot }) => (
            <div key={label} className={`${bg} rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm`}>
              {dot ? (
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: dot }} />
              ) : (
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gray-200" />
              )}
              <div>
                <p className={`text-2xl font-black leading-none ${color}`}>{count}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 font-medium">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task List View */}
      {viewMode === "list" && (
        <div className="space-y-4">
          {renderSection("todo", "To Do", tasksByStatus.todo)}
          {renderSection("in_progress", "In Progress", tasksByStatus.in_progress)}
          {renderSection("done", "Done", tasksByStatus.done)}
        </div>
      )}

      {/* Gantt View */}
      {viewMode === "gantt" && renderGanttView()}

      {/* Calendar View */}
      {viewMode === "calendar" && (calSubMode === "month" ? renderCalendarMonthView() : renderCalendarWeekView())}

      {/* Board View (Kanban-style) */}
      {viewMode === "board" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* To Do Column */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                <h3 className="font-semibold text-blue-700 text-sm">To Do</h3>
              </div>
              <span className="text-xs font-bold text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">{tasksByStatus.todo.length}</span>
            </div>
            <div className="space-y-2">
              {tasksByStatus.todo.map(task => {
                const boardAssignees = getAssignees(task);
                return (
                <Card key={task.id} className={`bg-white border-gray-100 shadow-sm relative group overflow-hidden border-l-4 ${priorityLeftBorder(task.priority)}`}>
                  <CardContent className="p-4">
                    {/* Hover actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
                      <button onClick={() => handleEdit(task)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-gray-700"><Pencil size={11} /></button>
                      <button onClick={() => handleDelete(task.id)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-red-500"><Trash2 size={11} /></button>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={task.status === "done" || task.completed}
                        onCheckedChange={() => toggleTaskComplete(task)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 pr-8">
                        <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                        {boardAssignees.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            {renderAvatarStack(boardAssignees, { size: 20, max: 3, borderColor: "white", fallbackBg: "#3B82F6" })}
                            <span className="text-xs text-gray-500 truncate">
                              {boardAssignees.length === 1 ? boardAssignees[0] : `${boardAssignees.length} staff`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {task.date && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar size={12} />
                              {format(parseISO(task.date), "d MMM yyyy")}
                            </span>
                          )}
                          {task.tag && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getTagStyle(task.tag)}`}>
                              {getTagLabel(task.tag)}
                            </span>
                          )}
                          {task.priority && (
                            <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${getPriorityStyle(task.priority)}`}>
                              <Flag size={10} />
                              {getPriorityLabel(task.priority)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {tasksByStatus.todo.length === 0 && (
                <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-400 text-sm">
                  No tasks
                </div>
              )}
            </div>
          </div>

          {/* In Progress Column */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <h3 className="font-semibold text-amber-700 text-sm">In Progress</h3>
              </div>
              <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{tasksByStatus.in_progress.length}</span>
            </div>
            <div className="space-y-2">
              {tasksByStatus.in_progress.map(task => {
                const boardAssigneesIP = getAssignees(task);
                return (
                <Card key={task.id} className={`bg-white border-gray-100 shadow-sm relative group overflow-hidden border-l-4 ${priorityLeftBorder(task.priority)}`}>
                  <CardContent className="p-4">
                    {/* Hover actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
                      <button onClick={() => handleEdit(task)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-gray-700"><Pencil size={11} /></button>
                      <button onClick={() => handleDelete(task.id)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-red-500"><Trash2 size={11} /></button>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={task.status === "done" || task.completed}
                        onCheckedChange={() => toggleTaskComplete(task)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 pr-8">
                        <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                        {boardAssigneesIP.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            {renderAvatarStack(boardAssigneesIP, { size: 20, max: 3, borderColor: "white", fallbackBg: "#F97316" })}
                            <span className="text-xs text-gray-500 truncate">
                              {boardAssigneesIP.length === 1 ? boardAssigneesIP[0] : `${boardAssigneesIP.length} staff`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {task.date && (
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar size={12} />
                              {format(parseISO(task.date), "d MMM yyyy")}
                            </span>
                          )}
                          {task.tag && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getTagStyle(task.tag)}`}>
                              {getTagLabel(task.tag)}
                            </span>
                          )}
                          {task.priority && (
                            <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${getPriorityStyle(task.priority)}`}>
                              <Flag size={10} />
                              {getPriorityLabel(task.priority)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {tasksByStatus.in_progress.length === 0 && (
                <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-400 text-sm">
                  No tasks
                </div>
              )}
            </div>
          </div>

          {/* Done Column */}
          <div className="space-y-3">
            <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <h3 className="font-semibold text-emerald-700 text-sm">Done</h3>
              </div>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{tasksByStatus.done.length}</span>
            </div>
            <div className="space-y-2">
              {tasksByStatus.done.map(task => {
                const boardAssigneesDone = getAssignees(task);
                return (
                <Card key={task.id} className="bg-gray-50 border-gray-100 shadow-sm relative group overflow-hidden border-l-4 border-l-emerald-300">
                  <CardContent className="p-4">
                    {/* Hover actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-10">
                      <button onClick={() => handleEdit(task)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-gray-700"><Pencil size={11} /></button>
                      <button onClick={() => handleDelete(task.id)} className="p-1 rounded bg-white shadow border border-gray-200 text-gray-400 hover:text-red-500"><Trash2 size={11} /></button>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={true}
                        onCheckedChange={() => toggleTaskComplete(task)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 pr-8">
                        <h4 className="font-medium text-gray-400 text-sm line-through">{task.title}</h4>
                        {boardAssigneesDone.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5 opacity-60">
                            {renderAvatarStack(boardAssigneesDone, { size: 20, max: 3, borderColor: "white", fallbackBg: "#9CA3AF" })}
                            <span className="text-xs text-gray-400 truncate">
                              {boardAssigneesDone.length === 1 ? boardAssigneesDone[0] : `${boardAssigneesDone.length} staff`}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {task.date && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Calendar size={12} />
                              {format(parseISO(task.date), "d MMM yyyy")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {tasksByStatus.done.length === 0 && (
                <div className="p-6 bg-gray-50 rounded-lg text-center text-gray-400 text-sm">
                  No tasks
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* My Tasks View */}
      {viewMode === "mytasks" && renderMyTasksView()}

      {/* Gantt hover tooltip */}
      {ganttTooltip && (() => {
        const { task, x, y } = ganttTooltip;
        const tooltipAssignees = getAssignees(task);
        const statusColors = {
          done: "bg-emerald-100 text-emerald-700",
          in_progress: "bg-amber-100 text-amber-700",
          todo: "bg-blue-100 text-blue-700",
        };
        const priorityColors = {
          high: "bg-red-100 text-red-600",
          mid: "bg-amber-100 text-amber-600",
          low: "bg-emerald-100 text-emerald-600",
        };
        const status = task.status || (task.completed ? "done" : "todo");
        const recLabel = getRecurrenceLabel(task.recurrence);
        // Position: prefer showing below-right of cursor, flip if near edge
        const tipW = 240;
        const tipX = x + 14 + tipW > window.innerWidth ? x - tipW - 8 : x + 14;
        const tipY = y + 10;
        return (
          <div
            className="pointer-events-none"
            style={{
              position: "fixed",
              left: tipX,
              top: tipY,
              width: tipW,
              zIndex: 9999,
              background: "white",
              border: "0.5px solid rgba(0,0,0,0.12)",
              borderRadius: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              padding: "12px 14px",
              fontSize: 12,
            }}
          >
            {/* Title */}
            <p className={`font-semibold text-gray-900 mb-2 leading-tight ${task._recurring ? "flex items-center gap-1.5" : ""}`}>
              {task._recurring && <RefreshCw size={11} className="text-blue-400 flex-shrink-0" />}
              {task.title}
            </p>

            {/* Assignees */}
            {tooltipAssignees.length > 0 && (
              <div className="flex items-center gap-2 mb-2">
                {renderAvatarStack(tooltipAssignees, { size: 20, max: 4, borderColor: "white", fallbackBg: "#60a5fa" })}
                <span className="text-gray-600 text-xs leading-tight">
                  {tooltipAssignees.join(", ")}
                </span>
              </div>
            )}

            {/* Dates */}
            <div className="flex items-center gap-1 text-gray-500 mb-2">
              <Calendar size={11} className="text-gray-400" />
              {task.start_date && task.start_date !== task.date
                ? <span>{task.start_date} → {task.date}</span>
                : <span>{task.date || "No date"}</span>
              }
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 mb-1">
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[status] || "bg-gray-100 text-gray-600"}`}>
                {status === "in_progress" ? "In Progress" : status === "done" ? "Done" : "To Do"}
              </span>
              {task.priority && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${priorityColors[task.priority] || "bg-gray-100 text-gray-600"}`}>
                  {task.priority === "high" ? "High" : task.priority === "low" ? "Low" : "Mid"}
                </span>
              )}
              {task.tag && (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">
                  {task.tag}
                </span>
              )}
            </div>

            {/* Recurrence */}
            {recLabel && (
              <div className="flex items-center gap-1 text-blue-500 text-[10px] mt-1.5">
                <RefreshCw size={9} />
                {recLabel}
              </div>
            )}

            {/* Location */}
            {task.location && (
              <p className="text-gray-400 text-[10px] mt-1 truncate flex items-center gap-1"><MapPin size={9}/>{task.location}</p>
            )}

            <p className="text-gray-300 text-[9px] mt-2">คลิกเพื่อแก้ไข</p>
          </div>
        );
      })()}
    </div>
  );
};

export default TasksPage;
